import {
  EpocusConsentAudience,
  EpocusConsentItemKind,
  EpocusConsentResponse,
} from "elephant-web/gql";
import {
  type ExtractDocumentTypeFromTypedRxJsonSchema,
  type RxCollection,
  type RxJsonSchema,
  toTypedRxJsonSchema,
} from "rxdb";
import { useRxCollection, type useRxData } from "rxdb-hooks";
import { SYNCABLE_META } from "./common";

export const RXDB_EPOCUS_PATIENT_CONSENT_COLLECTION_NAME =
  "epocus-patient-consent";

export const useRxCollectionEpocusPatientConsent =
  (): RxCollection<EpocusPatientConsent> | null =>
    useRxCollection<EpocusPatientConsent>(
      RXDB_EPOCUS_PATIENT_CONSENT_COLLECTION_NAME,
    );

type UseRxData = Parameters<typeof useRxData<EpocusPatientConsent>>;
export type EpocusPatientConsentQueryConstructor = UseRxData["1"];

// Template-literal-over-enum gives the literal union of the enum's runtime
// values, sourced from the generated schema rather than a hand-duplicated
// array — a new server-side kind shows up here automatically.
export type EpocusConsentItemKindValue = `${EpocusConsentItemKind}`;
export type EpocusConsentResponseValue = `${EpocusConsentResponse}`;
export type EpocusConsentAudienceValue = `${EpocusConsentAudience}`;

const EPOCUS_PATIENT_CONSENT_SCHEMA_LITERAL = {
  title: "epocus patient consent schema",
  version: 2,
  description:
    "One clinician-captured answer to a single consent question, anchored to an EpocusEncounter (ADR 0006 in api-imaging-study)",
  type: "object",
  primaryKey: "id",
  properties: {
    id: { type: "string", maxLength: 100 },
    encounterId: { type: "string", maxLength: 100 },
    elephantPatientIdentityValue: { type: ["string", "null"] },
    audience: { type: "string", enum: Object.values(EpocusConsentAudience) },
    kind: { type: "string", enum: Object.values(EpocusConsentItemKind) },
    response: { type: "string", enum: Object.values(EpocusConsentResponse) },
    consentDocumentId: { type: "string", maxLength: 100 },
    privacyPolicyId: { type: ["string", "null"], maxLength: 100 },
    programId: { type: ["string", "null"], maxLength: 100 },
    meta: SYNCABLE_META,
  },
  required: [
    "id",
    "encounterId",
    "audience",
    "kind",
    "response",
    "consentDocumentId",
    "meta",
  ],
  indexes: ["encounterId"],
  /*
   * biome-ignore lint/suspicious/noExplicitAny: We infer the DB type from this
   * object, the satisfies just provides us type inference for this object.
   */
} as const satisfies RxJsonSchema<any>;

const schemaTyped = toTypedRxJsonSchema(EPOCUS_PATIENT_CONSENT_SCHEMA_LITERAL);

type RawEpocusPatientConsent = ExtractDocumentTypeFromTypedRxJsonSchema<
  typeof schemaTyped
>;

export type EpocusPatientConsent = Omit<
  RawEpocusPatientConsent,
  "audience" | "kind" | "response"
> & {
  audience: EpocusConsentAudienceValue;
  kind: EpocusConsentItemKindValue;
  response: EpocusConsentResponseValue;
};

export type EpocusPatientConsentCollection = RxCollection<EpocusPatientConsent>;

export const EPOCUS_PATIENT_CONSENT_SCHEMA: RxJsonSchema<EpocusPatientConsent> =
  EPOCUS_PATIENT_CONSENT_SCHEMA_LITERAL as unknown as RxJsonSchema<EpocusPatientConsent>;
